import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Billing from "@/pages/Billing";
import Demo from "@/pages/Demo";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Settings from "@/pages/owner/Settings";
import PublicContract from "@/pages/PublicContract";
import PublicInvoice from "@/pages/PublicInvoice";
import Login from "@/pages/Login";
import OwnerLayout from "@/pages/owner/OwnerLayout";
import Dashboard from "@/pages/owner/Dashboard";
import Properties from "@/pages/owner/Properties";
import CalendarPage from "@/pages/owner/CalendarPage";
import Jobs from "@/pages/owner/Jobs";
import Cleaners from "@/pages/owner/Cleaners";
import Inventory from "@/pages/owner/Inventory";
import PhotosReview from "@/pages/owner/PhotosReview";
import Payouts from "@/pages/owner/Payouts";
import Invoices from "@/pages/owner/Invoices";
import Estimate from "@/pages/owner/Estimate";
import Leads from "@/pages/owner/Leads";
import Contracts from "@/pages/owner/Contracts";
import PremiumGuard from "@/components/PremiumGuard";

import CleanerLayout from "@/pages/cleaner/CleanerLayout";
import CleanerJobs from "@/pages/cleaner/CleanerJobs";
import CleanerEarnings from "@/pages/cleaner/CleanerEarnings";
import JobDetail from "@/pages/cleaner/JobDetail";

// Public root: send authenticated users to their dashboard, guests to the landing page.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (user) return <Navigate to={user.role === "cleaner" ? "/cleaner" : "/owner"} replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/estimate" element={<Navigate to="/demo" replace />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contract/:token" element={<PublicContract />} />
            <Route path="/invoice/:token" element={<PublicInvoice />} />
            <Route path="/login" element={<Login />} />

            <Route
              path="/owner/billing"
              element={
                <ProtectedRoute roles={["owner", "admin"]}>
                  <Billing />
                </ProtectedRoute>
              }
            />

            <Route
              path="/owner"
              element={
                <ProtectedRoute roles={["owner", "admin"]}>
                  <OwnerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="properties" element={<Properties />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="cleaners" element={<Cleaners />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="photos" element={<PhotosReview />} />
              <Route path="payouts" element={<Payouts />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="estimate" element={<PremiumGuard feature="AI Estimate Calculator"><Estimate /></PremiumGuard>} />
              <Route path="leads" element={<PremiumGuard feature="Leads"><Leads /></PremiumGuard>} />
              <Route path="contracts" element={<PremiumGuard feature="Contracts"><Contracts /></PremiumGuard>} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route
              path="/cleaner"
              element={
                <ProtectedRoute roles={["cleaner"]}>
                  <CleanerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CleanerJobs />} />
              <Route path="earnings" element={<CleanerEarnings />} />
              <Route path="jobs/:jobId" element={<JobDetail />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
